import { useMutation, UseMutationResult } from 'react-query';

import client from '@/apis/aniftyBackend/client';
import { V2CreationData } from '@/apis/aniftyBackend/creations/interface';
import { createQueryKey } from '@/apis/aniftyBackend/creations/like/useFetchCreationIsLiked';
import { createQueryKey as createV2CreationQueryKey } from '@/apis/aniftyBackend/creations/useV2FetchCreationByID';
import useCurrentUser from '@/hooks/useCurrentUser';
import { queryClient } from '@/utils/react-query';

const createMyLike = async (creationID: number): Promise<void> => {
  await client.post(`/api/creations/${creationID}/my_like`);
};

const tryToDeleteMyLike = async (creationID: number): Promise<void> => {
  try {
    await client.delete(`/api/creations/${creationID}/my_like`);
  } catch (e) {
    if (e?.response?.status === 404) {
      return;
    }
    throw e;
  }
};

const updateLikeCountOptimistically = (
  creationID: number,
  isLiked: boolean
) => {
  const key = createV2CreationQueryKey(creationID);

  const creation = queryClient.getQueryData<V2CreationData>(key);
  if (!creation) return;

  const diff = isLiked ? 1 : -1;

  queryClient.setQueryData(key, {
    ...creation,
    likeSummary: {
      ...creation.likeSummary,
      likeCount: creation.likeSummary.likeCount + diff,
    },
  });
};

const useToggleMyLike = (): UseMutationResult<boolean, unknown, number> => {
  const { user } = useCurrentUser();
  const userID = user?.userID;

  return useMutation({
    mutationFn: async (creationID: number) => {
      if (!userID) throw new Error('Not connected to Metamask');

      const isLiked = queryClient.getQueryData<boolean>(
        createQueryKey(creationID, userID)
      );
      if (isLiked === undefined) {
        throw new Error('The like status is not loaded');
      }

      if (isLiked) {
        await tryToDeleteMyLike(creationID);
      } else {
        await createMyLike(creationID);
      }

      return !isLiked;
    },
    onSuccess: async (isLiked, creationID) => {
      if (!userID) throw new Error('Not connected to Metamask');

      queryClient.setQueryData(createQueryKey(creationID, userID), isLiked);

      updateLikeCountOptimistically(creationID, isLiked);
      await queryClient.invalidateQueries(createV2CreationQueryKey(creationID));
    },
  });
};

export default useToggleMyLike;
